"use client";

import { useState, useEffect, useRef } from "react";
// import { DeviceType, getDeviceTypeByUA } from "@/app/utils";

const Tab = () => {
  const [activeTab, setActiveTab] = useState("教师智能助手");
  const [isSticky, setIsSticky] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const tabRef = useRef<HTMLDivElement>(null);
  const tabPlaceholderRef = useRef<HTMLDivElement>(null);
  const [tabHeight, setTabHeight] = useState(0);
  const [deviceType, setDeviceType] = useState<string>("other");

  const tabs = [
    { id: "教师智能助手", label: "教师智能助手", target: "services" },
    { id: "校园核心方案", label: "校园核心方案", target: "campus" },
    { id: "产品优势", label: "产品优势", target: "advantage" },
    { id: "合作伙伴", label: "合作伙伴", target: "cooperation" },
  ];

  // 在组件挂载时获取设备类型
  useEffect(() => {
    import("current-device").then((module) => {
      const deviceType = module.default.type;
      setDeviceType(deviceType);
      if (deviceType === "desktop") {
        setDeviceType("pc");
      }
      if (deviceType === "unknown") {
        setDeviceType("other");
      }
    });
  }, []);

  // 检测屏幕宽度
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(document.documentElement.clientWidth <= 768);
    };

    // 初始检查
    checkIfMobile();

    // 监听窗口大小变化
    window.addEventListener("resize", checkIfMobile);

    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // 初始化时保存Tab组件的高度
  useEffect(() => {
    if (tabRef.current) {
      setTabHeight(tabRef.current.offsetHeight);
    }
  }, []);
  // 根据滚动位置更新激活的选项卡
  const updateActiveTabOnScroll = () => {
    // 获取所有目标区域的位置信息
    const sectionPositions = tabs.map((tab) => {
      const section = document.getElementById(tab.target);
      if (!section) return { id: tab.id, top: 0, bottom: 0, height: 0 };

      const rect = section.getBoundingClientRect();
      return {
        id: tab.id,
        top: rect.top,
        bottom: rect.bottom,
        height: rect.height,
      };
    });

    // 计算视口的中心位置
    const viewportHeight = window.innerHeight;
    const viewportCenter = viewportHeight / 2;

    // 计算每个区域与视口中心的距离
    const distances = sectionPositions.map((section) => {
      // 计算区域中心点
      const sectionCenter = section.top + section.height / 2;
      // 计算与视口中心的距离（取绝对值）
      return {
        id: section.id,
        distance: Math.abs(sectionCenter - viewportCenter),
      };
    });

    // 找出距离视口中心最近的区域
    distances.sort((a, b) => a.distance - b.distance);

    // 视口中心在最顶部时，默认选中第一个选项卡
    if (window.scrollY < 200) {
      setActiveTab(tabs[0].id);
      return;
    }

    // 如果有区域在视口内，选中距离视口中心最近的区域
    if (distances.length > 0) {
      setActiveTab(distances[0].id);
    }
  };

  // 监听滚动事件来处理固定导航栏和激活状态
  useEffect(() => {
    const placeholder = tabPlaceholderRef.current;

    const handleScroll = () => {
      if (placeholder && tabRef.current) {
        const placeholderRect = placeholder.getBoundingClientRect();

        // 当占位符元素到达或超过视口顶部时，固定Tab组件（仅在非移动端）
        if (placeholderRect.top <= 0) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }

        // 获取各部分的位置信息，用于判断当前激活的选项卡
        updateActiveTabOnScroll();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const handleTabClick = (tabId: string, target: string) => {
    setActiveTab(tabId);

    // 滚动到目标组件
    const targetElement = document.getElementById(target);
    if (targetElement) {
      // 计算滚动位置，考虑固定Tab的高度
      const offset = isSticky ? tabHeight + 0 : 0;
      const targetPosition =
        targetElement.getBoundingClientRect().top + window.pageYOffset - offset;

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  };

  // 为每个选项卡添加不同的响应式样式
  const getResponsiveTabStyles = (tabId: string) => {
    // 基础样式
    const baseStyles = `
      relative z-10  text-[16px] leading-[16px]  transition-all duration-300
    `;

    // 激活状态样式
    if (activeTab === tabId) {
      return `${baseStyles} font-semibold text-[#1DBA55]`;
    }

    // 非激活状态样式
    return `${baseStyles} font-normal text-[#999999]`;
  };

  return (
    <>
      {/* 占位符元素，用于确定Tab组件的原始位置 */}
      <div
        ref={tabPlaceholderRef}
        className="w-full h-[70px] px-[80px] [@media(min-width:769px)_and_(max-width:1080px)]:px-[40px] [@media(max-width:768px)]:px-[20px]"
        style={{ height: isSticky ? `${tabHeight}px` : "0" }}
      ></div>

      {/* Tab组件 */}
      <div
        ref={tabRef}
        id="tab-section"
        className={`w-full h-[70px] px-[80px] [@media(min-width:769px)_and_(max-width:1080px)]:px-[40px] [@media(max-width:768px)]:px-[20px] bg-white mx-auto z-40
          ${isSticky ? "fixed top-0 left-0 right-0 shadow-md" : "relative"}
          transition-all duration-200 ease-in-out`}
      >
        {/* 中间导航栏 */}
        <div className="w-full max-w-[1360px]  [@media(max-width:660px)]:overflow-x-auto mx-auto flex flex-row items-center [@media(max-width:660px)]:[&::-webkit-scrollbar]:hidden">
          {tabs.map((tab, index) => (
            <div
              key={tab.id}
              className="relative max-px-[40px] flex-1 flex items-center justify-center py-0 w-full"
            >
              {/* 选项卡 */}
              <button
                onClick={() => {
                  handleTabClick(tab.id, tab.target);
                  window.Log.action(
                    "sub_tab_click",
                    {
                      sub_tab: `${tab.id}`,
                      user_access_port: deviceType,
                    },
                    "HWOfficialMainPage",
                  );
                }}
                className="w-full max-w-[180px] [@media(max-width:660px)]:w-[149px] min-h-[70px] py-[12px] px-[20px] [@media(max-width:768px)]:px-[14px] flex items-center justify-center transition-colors relative group cursor-pointer"
              >
                {/* 悬停背景 - 使用圆形背景 */}
                <span className="absolute inset-0 m-auto max-w-[120px] max-h-[40px] bg-gray-100/70 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>

                {/* 文字容器 - 根据激活状态应用不同样式 */}
                <span className={getResponsiveTabStyles(tab.id)}>
                  {tab.label}
                </span>

                {/* 绿色短线 - 仅在激活状态显示 */}
                {activeTab === tab.id && (
                  <span className="absolute bottom-1 h-[2px] w-full max-w-[84px] bg-[#1DBA55] rounded-full transition-all duration-300"></span>
                )}
              </button>

              {/* 分隔线 - 除了最后一个选项外都显示，在小屏幕上隐藏 */}
              {index < tabs.length - 1 && (
                <div className="block absolute right-0 top-1/2 transform -translate-y-1/2 h-[16px] md:h-[18px] lg:h-[20px] xl:h-[24px] border-r border-[#E9E9E9]"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default Tab;
